'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface RelatedLink {
  title: string;
  url: string;
  type: 'case-study' | 'blog' | 'service';
}

interface FAQItemProps {
  question: string;
  answer: string;
  relatedLinks?: RelatedLink[];
  videoUrl?: string;
  onFeedback?: (helpful: boolean) => void;
}

const FAQItem = ({ question, answer, relatedLinks, videoUrl, onFeedback }: FAQItemProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFeedback = (helpful: boolean) => {
    if (!isHydrated) return;
    
    setFeedbackGiven(true);
    if (onFeedback) {
      onFeedback(helpful);
    }
  };

  const getLinkIcon = (type: string) => {
    switch (type) {
      case 'case-study':
        return 'DocumentTextIcon';
      case 'blog':
        return 'NewspaperIcon';
      case 'service':
        return 'CubeIcon';
      default:
        return 'LinkIcon';
    }
  };

  if (!isHydrated) {
    return (
      <div className="glassmorphic rounded-xl overflow-hidden mb-4 shadow-silver-glow">
        <div className="p-6 cursor-pointer">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-foreground pr-8">{question}</h3>
            <Icon name="ChevronDownIcon" size={24} className="text-muted-foreground flex-shrink-0" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glassmorphic rounded-xl overflow-hidden mb-4 transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 text-left"
      >
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-semibold text-foreground pr-8">{question}</h3>
          <Icon 
            name="ChevronDownIcon" 
            size={24} 
            className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-border">
          <div className="pt-6 space-y-6">
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed">{answer}</p>
            </div>

            {videoUrl && (
              <div className="bg-muted rounded-lg p-4 flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="PlayIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Video uitleg beschikbaar</p>
                  <p className="text-xs text-muted-foreground">Bekijk gedetailleerde uitleg</p>
                </div>
              </div>
            )}

            {relatedLinks && relatedLinks.length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-3">Gerelateerde bronnen:</p>
                <div className="space-y-2">
                  {relatedLinks.map((link, index) => (
                    <Link
                      key={index}
                      href={link.url}
                      className="flex items-center space-x-3 p-3 bg-muted hover:bg-primary/10 rounded-lg transition-all duration-300 group"
                    >
                      <Icon 
                        name={getLinkIcon(link.type) as any} 
                        size={20} 
                        className="text-muted-foreground group-hover:text-primary transition-colors duration-300"
                      />
                      <span className="text-sm text-foreground group-hover:text-primary transition-colors duration-300">
                        {link.title}
                      </span>
                      <Icon 
                        name="ArrowRightIcon" 
                        size={16} 
                        className="text-muted-foreground group-hover:text-primary transition-colors duration-300 ml-auto"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">Was dit antwoord nuttig?</p>
              {!feedbackGiven ? (
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleFeedback(true)}
                    className="px-4 py-2 bg-muted hover:bg-success/20 text-foreground hover:text-success rounded-lg transition-all duration-300 flex items-center space-x-2 shadow-silver hover:shadow-green-glow"
                  >
                    <Icon name="HandThumbUpIcon" size={18} />
                    <span className="text-sm">Ja</span>
                  </button>
                  <button
                    onClick={() => handleFeedback(false)}
                    className="px-4 py-2 bg-muted hover:bg-error/20 text-foreground hover:text-error rounded-lg transition-all duration-300 flex items-center space-x-2 shadow-silver"
                  >
                    <Icon name="HandThumbDownIcon" size={18} />
                    <span className="text-sm">Nee</span>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2 text-success">
                  <Icon name="CheckCircleIcon" size={20} />
                  <span className="text-sm">Bedankt voor je feedback!</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQItem;